import { useState } from 'react';
import {
  Badge,
  Button,
  Dialog,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  useAppointment,
  useAppointmentActions,
  useDoctors,
} from '@/features/queue/api';
import type { AppointmentDetail } from '@/features/queue/types';
import { useNow } from '@/features/shell/use-now';
import { isApiError } from '@/lib/api';
import {
  AppCheckInSummary,
  LateNoticeNote,
} from '@/pages/queue/AppCheckInSummary';
import { STATUS_LABEL, bookingConflict, depositLabel } from './booking-format';
import { dayLabel, klDate, klTime, toIso, todayKl, whenLabel } from './time';
import styles from './calendar.module.css';
import { PatientSays } from '@/features/queue/PatientSays';

/**
 * Booking details (UAT RL-706: details and the post-booking actions):
 * check in (today), reschedule, cancel (frees the slot for the waitlist),
 * no-show, deposit taken, the series, the arrival pass and the messages sent.
 */
export function BookingDrawer({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}) {
  const { data, isLoading, error, refetch } = useAppointment(id);
  return (
    <Dialog open={!!id} onClose={onClose} title="Booking">
      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {data && (
        <Body key={`${data.id}:${data.version}`} a={data} onClose={onClose} />
      )}
    </Dialog>
  );
}

function Body({ a, onClose }: { a: AppointmentDetail; onClose: () => void }) {
  const { hasPermission } = useSession();
  const canManage = hasPermission('appointments:manage');
  const act = useAppointmentActions();
  const { data: doctors } = useDoctors();
  const toast = useToast();
  const [moving, setMoving] = useState(false);
  const [date, setDate] = useState(klDate(a.startsAt));
  const [time, setTime] = useState(klTime(a.startsAt));
  const [doctor, setDoctor] = useState(a.doctor?.id ?? '');
  const [cancelling, setCancelling] = useState(false);
  const [moveProblem, setMoveProblem] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const [reason, setReason] = useState('');
  const fail = (err: unknown) =>
    toast.show({
      tone: 'error',
      title: isApiError(err) ? (err.problem?.title ?? 'Not done') : 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
  const booked = a.status === 'booked';
  const today = klDate(a.startsAt) === todayKl();
  const now = useNow();
  const past = new Date(a.startsAt).getTime() < now.getTime();

  return (
    <div className={styles.stack}>
      {a.rebook && booked && (
        <p className={styles.note}>
          Asked to rebook:{' '}
          {a.rebook.event?.description ?? 'the calendar changed'}. Move it to a
          new time.
        </p>
      )}
      <PatientSays reason={a.patientReason} />
      <dl className={styles.rows}>
        <dt>Patient</dt>
        <dd className={styles.name}>{a.patient.fullName}</dd>
        <dt>When</dt>
        <dd>{whenLabel(a.startsAt)}</dd>
        <dt>Branch</dt>
        <dd>{a.branch.name}</dd>
        <dt>Service</dt>
        <dd>{a.service.name}</dd>
        <dt>Doctor</dt>
        <dd>{a.doctor?.fullName ?? 'Any'}</dd>
        {a.travelDate && (
          <>
            <dt>Travel</dt>
            <dd>{dayLabel(a.travelDate)}</dd>
          </>
        )}
        <dt>Deposit</dt>
        <dd>{depositLabel(a)}</dd>
        <dt>Status</dt>
        <dd>
          <Badge tone={a.status === 'checked_in' ? 'success' : 'neutral'}>
            {a.status === 'checked_in' && a.arrivedLate
              ? 'Arrived late'
              : STATUS_LABEL[a.status]}
          </Badge>
          {a.queueTicket && ` · ticket ${a.queueTicket.number}`}
        </dd>
        <dt>No-shows</dt>
        <dd>{a.noShows}</dd>
        {a.patient.arrivalPassToken && (
          <>
            <dt>Counter code</dt>
            <dd>
              <span className={styles.mono}>{a.patient.arrivalPassToken}</span>
              <br />
              <span className={styles.sub}>
                {/* RL-1668 (gap #131): not a test token; app users show the pass in the app. */}
                Type it at the counter scanner when the patient cannot show the
                arrival pass in the app.
              </span>
            </dd>
          </>
        )}
      </dl>

      <LateNoticeNote notice={a.lateNotice} />
      <AppCheckInSummary
        checkIn={a.appCheckIn}
        showEmpty
        shownReason={a.patientReason}
      />

      {a.series.length > 1 && (
        <div className={styles.section}>
          <p className="eyebrow">Series</p>
          <ul className={styles.messages}>
            {a.series.map((s) => (
              <li key={s.id}>
                {whenLabel(s.startsAt)} · {STATUS_LABEL[s.status]}
              </li>
            ))}
          </ul>
        </div>
      )}

      {canManage && booked && (
        <div className={styles.actions}>
          {today && (
            <Button
              variant="primary"
              loading={act.checkIn.isPending}
              onClick={() =>
                act.checkIn
                  .mutateAsync(a.id)
                  .then((r) => {
                    toast.show({
                      tone: r.arrivedLate ? 'info' : 'success',
                      title: `Arrived · ticket ${r.queueTicket?.number ?? ''}`,
                      body: r.arrivedLate
                        ? 'More than 15 minutes late: queued with walk-in priority. Tell the patient now.'
                        : undefined,
                    });
                    if (r.payerNotice)
                      toast.show({
                        tone: 'error',
                        title: 'Who pays: ask the patient',
                        body: r.payerNotice.message,
                      });
                    onClose();
                  })
                  .catch(fail)
              }
            >
              Check in · Arrived
            </Button>
          )}
          {a.deposit?.status === 'pending' && (
            <>
              <Button
                loading={act.depositPaid.isPending}
                onClick={() =>
                  act.depositPaid
                    .mutateAsync({ id: a.id, method: 'cash' })
                    .catch(fail)
                }
              >
                Deposit paid · cash
              </Button>
              <Button
                loading={act.depositPaid.isPending}
                onClick={() =>
                  act.depositPaid
                    .mutateAsync({ id: a.id, method: 'card' })
                    .catch(fail)
                }
              >
                Deposit paid · card
              </Button>
            </>
          )}
          <Button onClick={() => setMoving((m) => !m)}>Reschedule</Button>
          {past && (
            <Button
              loading={act.noShow.isPending}
              onClick={() => act.noShow.mutateAsync(a.id).catch(fail)}
            >
              No-show
            </Button>
          )}
          <Button variant="danger" onClick={() => setCancelling((c) => !c)}>
            Cancel booking
          </Button>
        </div>
      )}

      {moving && (
        <div className={styles.section} aria-label="Reschedule">
          <div className={styles.split}>
            <Field label="New date">
              <Input
                type="date"
                value={date}
                min={todayKl()}
                onChange={(e) => setDate(e.target.value)}
              />
            </Field>
            <Field label="New time">
              <Input
                type="time"
                value={time}
                step={300}
                onChange={(e) => setTime(e.target.value)}
              />
            </Field>
          </div>
          <Field label="Doctor">
            <Select
              value={doctor}
              onChange={(e) => setDoctor(e.target.value)}
              options={[
                { value: '', label: 'Any' },
                ...(doctors ?? []).map((d) => ({
                  value: d.id,
                  label: d.fullName,
                })),
              ]}
            />
          </Field>
          {moveProblem && (
            <p className={styles.note} role="alert">
              <strong>{moveProblem.title}</strong>
              <br />
              {moveProblem.message}
            </p>
          )}
          <Button
            variant="primary"
            loading={act.reschedule.isPending}
            onClick={() => {
              setMoveProblem(null);
              void act.reschedule
                .mutateAsync({
                  id: a.id,
                  startsAt: toIso(date, time),
                  doctorStaffId: doctor || null,
                })
                .then((r) => {
                  toast.show({
                    tone: 'success',
                    title: `Moved to ${whenLabel(r.startsAt)}`,
                    body: 'The old slot is offered to the waitlist.',
                  });
                  setMoving(false);
                })
                .catch((err: unknown) => {
                  // Kept in the drawer so the time already taken stays visible.
                  const conflict = bookingConflict(err);
                  if (conflict) setMoveProblem(conflict);
                  else fail(err);
                });
            }}
          >
            Move booking
          </Button>
        </div>
      )}

      {cancelling && (
        <div className={styles.section} aria-label="Cancel booking">
          <Field label="Reason">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <p className={styles.sub}>
            Cancelling frees the slot for the waitlist. A paid deposit is
            refunded if cancelled at least 24 hours before; otherwise it is
            kept.
          </p>
          <Button
            variant="danger"
            disabled={reason.trim().length < 2}
            loading={act.cancel.isPending}
            onClick={() =>
              act.cancel
                .mutateAsync({ id: a.id, reason: reason.trim() })
                .then(() => {
                  toast.show({ tone: 'success', title: 'Booking cancelled' });
                  onClose();
                })
                .catch(fail)
            }
          >
            Cancel booking
          </Button>
        </div>
      )}

      {a.messages.length > 0 && (
        <div className={styles.section}>
          <p className="eyebrow">Messages to the patient</p>
          <ul className={styles.messages}>
            {a.messages.map((m) => (
              <li key={m.id}>
                <Badge tone={m.status === 'suppressed' ? 'warning' : 'info'}>
                  {m.status === 'suppressed'
                    ? `Not sent (${m.suppressedReason?.replace(/_/g, ' ')})`
                    : `${m.channel.toUpperCase()} · recorded`}
                </Badge>{' '}
                {m.body}
              </li>
            ))}
          </ul>
          <p className={styles.sub}>
            No SMS, WhatsApp or push provider is connected yet: messages are
            recorded in the outbox.
          </p>
        </div>
      )}
    </div>
  );
}
